'use client';

import React, { useContext, useEffect, useRef, useState } from 'react';
import { FaChevronDown } from 'react-icons/fa6';
import { useVirtualizer } from '@tanstack/react-virtual';
import { SessionContext } from '@benzinga/session-context';
import { WatchlistManager, Watchlist, WatchlistSymbol } from '@benzinga/watchlist-manager';
import { QuotesManager, DelayedQuote } from '@benzinga/quotes-manager';
import { Button, ErrorBoundary, Spinner } from '@benzinga/core-ui';
import { formatPrice } from '@benzinga/utils';
import { BlockLayout } from './BlockLayout';

type WatchlistSymbolWithQuote = WatchlistSymbol & { quote?: DelayedQuote };
type WatchlistWithQuotes = Omit<Watchlist, 'symbols'> & { symbols: WatchlistSymbolWithQuote[] };

const ROW_HEIGHT = 53;

interface WatchlistSelectProps {
  isDropdownOpen: boolean;
  onToggleDropdown: () => void;
  onSelectWatchlist: (watchlist: Watchlist) => void;
  selectedWatchlist: WatchlistWithQuotes | null;
  watchlists: Watchlist[];
}

const WatchlistSelect = React.memo(
  ({ isDropdownOpen, onToggleDropdown, onSelectWatchlist, selectedWatchlist, watchlists }: WatchlistSelectProps) => (
    <div className="relative mb-3">
      <button
        onClick={onToggleDropdown}
        className="flex items-center gap-2 px-3 py-2 w-full bg-white border border-gray-200 rounded-md text-sm font-medium text-gray-900 hover:bg-gray-50 transition-colors"
      >
        <span className="whitespace-nowrap truncate w-full max-w-[220px] text-left">
          {selectedWatchlist?.name || 'Select Watchlist'}
        </span>
        <span className="text-gray-400 text-xs bg-gray-100 px-1.5 py-0.5 rounded ml-auto">
          {selectedWatchlist?.symbols.length || 0}
        </span>
        <FaChevronDown
          size={12}
          className={`text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isDropdownOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-[2] w-full max-h-[300px] overflow-y-auto">
          {watchlists.map(watchlist => (
            <button
              key={`watchlist-item-${watchlist.watchlistId}`}
              onClick={() => onSelectWatchlist(watchlist)}
              className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors flex items-center justify-between"
            >
              <span className="text-sm font-medium text-gray-900">{watchlist.name}</span>
              <span className="text-xs text-gray-500 min-w-[40px] ml-auto text-right">{watchlist.symbols.length}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  ),
);

const PriceLoadingIndicator = () => (
  <div className="flex flex-col items-end gap-1">
    <div className="w-12 h-4 bg-gray-200 rounded animate-pulse" />
    <div className="w-16 h-3 bg-gray-200 rounded animate-pulse" />
  </div>
);

interface VirtualizedSymbolListProps {
  symbols: WatchlistSymbolWithQuote[];
  isLoadingQuotes: boolean;
}

const VirtualizedSymbolList = ({ symbols, isLoadingQuotes }: VirtualizedSymbolListProps) => {
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: symbols.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 5,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  if (symbols.length === 0) return null;

  return (
    <div ref={parentRef} className="overflow-y-auto flex-1">
      <div className="relative w-full" style={{ height: totalSize }}>
        {virtualItems.map(virtualRow => {
          const watchlistSymbol = symbols[virtualRow.index];
          const change = watchlistSymbol.quote?.change ?? 0;
          const changePercent = watchlistSymbol.quote?.changePercent ?? 0;
          const price = watchlistSymbol.quote?.lastTradePrice ?? 0;
          const hasQuoteData = watchlistSymbol.quote !== undefined && price !== 0;
          const hasChangeData = hasQuoteData && (change !== 0 || changePercent !== 0);
          const direction = change > 0 ? 'up' : change < 0 ? 'down' : 'none';
          const changeIcon = change > 0 ? '▲' : change < 0 ? '▼' : '';
          const changeColorClass =
            direction === 'up' ? 'text-green-600' : direction === 'down' ? 'text-red-600' : 'text-gray-400';
          const showPriceLoading = isLoadingQuotes && !hasQuoteData;

          return (
            <a
              key={`watchlists-watchlist-item-${watchlistSymbol.symbol}`}
              href={`/quote/${watchlistSymbol.symbol.toLowerCase()}`}
              className="absolute left-0 w-full flex items-center justify-between py-2 px-3 bg-white hover:bg-gray-50 border-b border-gray-100 transition-colors"
              style={{
                height: ROW_HEIGHT,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <div className="flex flex-col">
                <div className="font-semibold text-gray-900 text-sm">{watchlistSymbol.symbol}</div>
                <div className="text-xs text-gray-500 truncate max-w-[120px]">
                  {watchlistSymbol.quote?.name || watchlistSymbol.symbol}
                </div>
              </div>
              {showPriceLoading ? (
                <PriceLoadingIndicator />
              ) : (
                <div className="flex flex-col items-end">
                  <div className="font-semibold text-gray-900 text-sm">
                    {hasQuoteData ? `$${formatPrice(price)}` : '-'}
                  </div>
                  <div className={`text-xs font-medium flex items-center gap-1 ${changeColorClass}`}>
                    {hasChangeData ? (
                      <>
                        <span>{changeIcon}</span>
                        <span>${change ? Math.abs(change).toFixed(2) : '-'}</span>
                        <span className="text-gray-400">|</span>
                        <span>{changePercent ? changePercent.toFixed(2) : '-'}%</span>
                      </>
                    ) : (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>
                </div>
              )}
            </a>
          );
        })}
      </div>
    </div>
  );
};

export const WatchlistWidget = () => {
  const session = useContext(SessionContext);
  const watchlistManager = session.getManager(WatchlistManager);
  const quotesManager = session.getManager(QuotesManager);

  const [watchlists, setWatchlists] = useState<Watchlist[]>([]);
  const [selectedWatchlist, setSelectedWatchlist] = useState<WatchlistWithQuotes | null>(null);
  const [isLoadingWatchlists, setIsLoadingWatchlists] = useState(true);
  const [isLoadingQuotes, setIsLoadingQuotes] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const loadWatchlists = async () => {
      setIsLoadingWatchlists(true);
      const watchlistsResult = await watchlistManager.getWatchlists();

      const loadedWatchlists = watchlistsResult.ok;
      if (loadedWatchlists && loadedWatchlists.length > 0) {
        setWatchlists(loadedWatchlists);
        setSelectedWatchlist(prev => {
          if (prev) return prev;
          const firstWatchlist = loadedWatchlists[0];
          return {
            ...firstWatchlist,
            symbols: (firstWatchlist.symbols || []).map(symbol => ({ ...symbol })),
          };
        });
      }
      setIsLoadingWatchlists(false);
    };

    loadWatchlists();
  }, [watchlistManager]);

  const selectedWatchlistId = selectedWatchlist?.watchlistId;

  useEffect(() => {
    if (!selectedWatchlistId) return;

    const loadQuotesForSelectedWatchlist = async () => {
      setIsLoadingQuotes(true);
      setSelectedWatchlist(currentWatchlist => {
        if (!currentWatchlist) return currentWatchlist;

        const symbols = currentWatchlist.symbols.map(symbol => symbol.symbol);
        if (symbols.length === 0) {
          setIsLoadingQuotes(false);
          return currentWatchlist;
        }

        quotesManager.getDelayedQuotes(symbols).then(quotesResult => {
          if (quotesResult.ok) {
            setSelectedWatchlist(prev => {
              if (!prev || prev.watchlistId !== currentWatchlist.watchlistId) return prev;
              return {
                ...prev,
                symbols: prev.symbols.map(symbol => ({
                  ...symbol,
                  quote: quotesResult.ok[symbol.symbol],
                })),
              };
            });
          }
          setIsLoadingQuotes(false);
        });

        return currentWatchlist;
      });
    };

    loadQuotesForSelectedWatchlist();
  }, [quotesManager, selectedWatchlistId]);

  const renderEmptyState = () => (
    <div
      className="relative flex flex-col items-start justify-center py-4 px-4 rounded-xl text-left overflow-hidden
        bg-[#1D222C] bg-[url('/next-assets/images/watchlist-widget-background.png')] bg-right bg-no-repeat bg-contain"
    >
      <div className="absolute inset-0 bg-gradient-to-tl from-transparent to-[#1D222C] to-50%" />
      <div className="relative">
        <div className="inline-block mb-4 px-1.5 py-[0.05rem] bg-blue-50 backdrop-blur-sm rounded">
          <span className="text-blue-500 text-sm font-bold tracking-wide">FREE</span>
        </div>

        <h3 className="text-white text-lg font-semibold mb-1 leading-tight">
          CREATE A WATCHLIST TO KEEP UP WITH YOUR STOCKS
        </h3>

        <p className="text-bzgray-300 text-sm mb-6 max-w-md leading-relaxed">
          Track your favorite stocks, news, insider trades, events, and the latest company info.
        </p>

        <Button
          as="a"
          className="w-full uppercase py-3"
          href="/profile/portfolio?action=create-watchlist"
          variant="flat-blue"
        >
          Create a Watchlist
        </Button>
      </div>
    </div>
  );

  const handleToggleDropdown = React.useCallback(() => {
    setIsDropdownOpen(prev => !prev);
  }, []);

  const handleSelectWatchlist = React.useCallback(
    (watchlist: Watchlist) => {
      if (watchlist.watchlistId === selectedWatchlist?.watchlistId) {
        setIsDropdownOpen(false);
        return;
      }
      setSelectedWatchlist({
        ...watchlist,
        symbols: (watchlist.symbols || []).map(symbol => ({ ...symbol })),
      });
      setIsDropdownOpen(false);
    },
    [selectedWatchlist?.watchlistId],
  );

  const renderNoSymbolsState = () => (
    <div className="flex flex-col items-center justify-center h-full px-6 text-center">
      <h3 className="text-bzgray-900 text-lg font-bold leading-tight uppercase mb-2">No Symbols in Watchlist</h3>
      <p className="text-bzgray-600 text-sm mb-4 leading-relaxed">
        Add stocks to your watchlist to track their prices, news, and more.
      </p>
      <Button as="a" variant="flat-blue" className="uppercase" href="/profile/portfolio">
        Add Symbols
      </Button>
    </div>
  );

  const renderWatchlistContent = () => {
    if (!selectedWatchlist) return null;
    return (
      <div className="flex flex-col h-full">
        <WatchlistSelect
          isDropdownOpen={isDropdownOpen}
          onToggleDropdown={handleToggleDropdown}
          onSelectWatchlist={handleSelectWatchlist}
          selectedWatchlist={selectedWatchlist}
          watchlists={watchlists}
        />
        {selectedWatchlist.symbols.length === 0 && renderNoSymbolsState()}
        <VirtualizedSymbolList symbols={selectedWatchlist.symbols} isLoadingQuotes={isLoadingQuotes} />
      </div>
    );
  };

  if (watchlists.length === 0) {
    return renderEmptyState();
  }

  return (
    <ErrorBoundary name="watchlist-widget">
      <BlockLayout
        title="WATCHLISTS"
        actionLabel="Customize"
        actionLink="/profile/portfolio"
        showChevron
        variant="default"
      >
        <div className="h-[400px]">
          {isLoadingWatchlists ? (
            <div className="flex flex-col items-center justify-center h-full">
              <span>
                <Spinner />
              </span>
              <div className="text-gray-500 text-sm mt-3">Loading watchlists...</div>
            </div>
          ) : (
            renderWatchlistContent()
          )}
        </div>
      </BlockLayout>
    </ErrorBoundary>
  );
};

export default WatchlistWidget;
